import { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SearchInput, UserAvatarsStack, spacing } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';
import { withTranslation } from '../../../../locales/hoc';
import { OrganisationMemberDataType, OrganisationMembersTable } from '../OrganisationMembersTable';
import { EmailCell } from '../EmailCell';
import { FamilyCaregiverConnectionsModal } from './FamilyCaregiverConnectionsModal';
import { PressableCell } from '../../../components/PressableCell';
import { useGoToProfile } from '../../../components/useGoToProfile';
import { UsersContainer } from '../UsersContainer';
import { Spacer } from '../Spacer';
import { useMemberSearch } from '../useMemberSearch';

type Props = { data: OrganisationMemberDataType[] };

export const FamilyCaregiversTable = withTranslation(({ data }: Props) => {
	const { t } = useTranslation();
	const goToProfile = useGoToProfile();
	const { getHeaders } = useAuthHeaders();
	const [caregiverForModal, setCaregiverForModal] = useState<OrganisationMemberDataType>();
	const { searchInput, filteredData } = useMemberSearch({ data });

	const openCaregiverConnectionsModal = useCallback(
		(caregiver: OrganisationMemberDataType) => () => {
			setCaregiverForModal(caregiver);
		},
		[],
	);

	const closeCaregiverConnectionsModal = useCallback(() => {
		setCaregiverForModal(undefined);
	}, []);

	const removeCareRecipientFromGuardianOf = useCallback(
		({
			caregiverUuid,
			careRecipientUuid,
		}: {
			caregiverUuid: string;
			careRecipientUuid: string;
		}) => {
			if (caregiverForModal?.uuid !== caregiverUuid) {
				return;
			}

			const updatedGuardianOf = caregiverForModal.guardianOf?.filter(
				({ uuid }) => uuid !== careRecipientUuid,
			);

			setCaregiverForModal({ ...caregiverForModal, guardianOf: updatedGuardianOf });
		},
		[caregiverForModal],
	);

	const columnsConfig = useMemo(
		() => [
			{
				name: t('table.header.name'),
				accessor: 'firstName' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<PressableCell text={getName(row)} onPress={() => goToProfile(row)} />
				),
			},
			{
				name: t('table.header.username'),
				accessor: 'username' as const,
			},
			{
				name: t('table.header.email'),
				accessor: 'email' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => <EmailCell email={row.email} />,
			},
			{
				name: t('tab-label-care-recipients'),
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<UserAvatarsStack
						maxAvatars={4}
						avatarSize={32}
						items={
							row.guardianOf?.map(({ photoUrl, ...member }) => ({
								name: getName(member),
								photoSource: photoUrl
									? { uri: photoUrl, headers: getHeaders(photoUrl) }
									: undefined,
								onPress: openCaregiverConnectionsModal(row),
							})) ?? []
						}
						style={styles.careReceiversAvatars}
					/>
				),
			},
		],
		[t, goToProfile, getHeaders, openCaregiverConnectionsModal],
	);

	return (
		<UsersContainer>
			<UsersContainer.Header>
				<SearchInput {...searchInput} />
				<Spacer />
			</UsersContainer.Header>
			<OrganisationMembersTable data={filteredData} columnsConfig={columnsConfig} />

			<FamilyCaregiverConnectionsModal
				visible={!!caregiverForModal}
				onClose={closeCaregiverConnectionsModal}
				caregiver={caregiverForModal}
				onDisconnectSuccess={removeCareRecipientFromGuardianOf}
			/>
		</UsersContainer>
	);
});

const styles = StyleSheet.create({
	careReceiversAvatars: { paddingHorizontal: spacing() },
});
